import React, { useState, useEffect } from 'react';
import { subscribeToPush, getNotificationPermission } from '../services/pushNotificationService';

const PROMPT_KEY = 'push_permission_asked';

export default function NotificationPermissionPrompt() {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // 'granted' | 'denied' | 'error'

  useEffect(() => {
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (getNotificationPermission() !== 'default') return;
    if (localStorage.getItem(PROMPT_KEY)) return;

    /* Delay slightly after login to avoid flash */
    const t = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(PROMPT_KEY, String(Date.now()));
  };

  const enable = async () => {
    setLoading(true);
    const outcome = await subscribeToPush();
    setLoading(false);
    setResult(outcome);
    localStorage.setItem(PROMPT_KEY, String(Date.now()));
    if (outcome === 'granted') {
      setTimeout(() => setVisible(false), 2000);
    }
  };

  if (!visible) return null;

  return (
    <div style={{
      position:   'fixed', inset: 0, zIndex: 9990,
      background: 'rgba(0,0,0,0.55)', display: 'flex',
      alignItems: 'flex-end', justifyContent: 'center',
      padding:    '0 0 24px',
    }}>
      <div style={{
        background:    '#fff', borderRadius: 20, padding: '28px 24px',
        width: '100%', maxWidth: 440,
        boxShadow:     '0 -4px 40px rgba(0,0,0,0.2)',
        animation:     'slideUp 0.25s ease',
      }}>
        {result === 'granted' ? (
          <>
            <div style={{ textAlign: 'center', fontSize: 44, marginBottom: 10 }}>✅</div>
            <h2 style={{ textAlign: 'center', fontSize: 18, fontWeight: 700, color: '#22c55e', marginBottom: 8 }}>
              Notifications enabled!
            </h2>
            <p style={{ textAlign: 'center', color: '#64748b', fontSize: 14 }}>
              You'll get instant alerts for leads, demos, and tasks.
            </p>
          </>
        ) : result === 'denied' ? (
          <>
            <div style={{ textAlign: 'center', fontSize: 44, marginBottom: 10 }}>🔕</div>
            <h2 style={{ textAlign: 'center', fontSize: 18, fontWeight: 700, color: '#ef4444', marginBottom: 8 }}>
              Notifications blocked
            </h2>
            <p style={{ textAlign: 'center', color: '#64748b', fontSize: 14, marginBottom: 20 }}>
              Enable notifications in your browser settings to receive CRM alerts.
            </p>
            <button onClick={dismiss} style={primaryBtn}>Close</button>
          </>
        ) : (
          <>
            {/* Bell icon */}
            <div style={{ textAlign: 'center', fontSize: 44, marginBottom: 12 }}>🔔</div>

            <h2 style={{ textAlign: 'center', fontSize: 19, fontWeight: 800, color: '#1B2A6B', marginBottom: 6 }}>
              Stay on top of your leads
            </h2>
            <p style={{ textAlign: 'center', color: '#64748b', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
              Get instant alerts even when the app is closed
            </p>

            {/* Benefit list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24, padding: '0 8px' }}>
              {[
                ['🎯', 'New lead assigned', 'Know instantly when a lead lands in your queue'],
                ['📅', 'Follow-up reminders', 'Never miss a scheduled follow-up'],
                ['🎬', 'Demo reminders', '24h and 1h alerts before every demo'],
              ].map(([icon, title, desc]) => (
                <div key={title} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <span style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{title}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button onClick={enable} disabled={loading} style={primaryBtn}>
                {loading ? 'Enabling…' : 'Enable Notifications'}
              </button>
              <button onClick={dismiss} style={ghostBtn}>Not now</button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(60px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}

const primaryBtn = {
  width: '100%', padding: '13px 0', borderRadius: 12, border: 'none',
  background: '#1B2A6B', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer',
};

const ghostBtn = {
  width: '100%', padding: '11px 0', borderRadius: 12,
  border: '1.5px solid #e2e8f0', background: 'transparent',
  color: '#94a3b8', fontWeight: 600, fontSize: 14, cursor: 'pointer',
};
